import { Box } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { FieldErrorMessage } from "@outschool/ui-components-shared";
import { HorizontalLineWithContent } from "@outschool/ui-legacy-component-library";
import { Field, FieldDefinition, useFormState } from "@outschool/ui-utils";
import * as StripeLib from "@stripe/stripe-js";
import React from "react";

import {
  StripeCardBuyerName,
  StripeCardCvc,
  StripeCardExpiry,
  StripeCardNumber,
  StripeCardZip,
  StripePaymentRequestButton,
  Variants
} from "../lib/Stripe";
import { BillingDetails } from "./Stripe";

const ROW_HEIGHT = "88px";

const LabelBox = ({ children }) => (
  <Box
    sx={{
      fontWeight: "fontWeightMedium",
      fontSize: "0.875em",
      lineHeight: "21px",
      color: "grey.700",
      marginBottom: "0.5em"
    }}
  >
    {children}
  </Box>
);

interface StripeElementValue {
  elementType?: string;
  empty?: boolean;
  complete?: boolean;
  error?: {
    message: string;
    code: string;
    type: string;
  };
}

function getVariant(field: Field<StripeElementValue>): Variants {
  return field.value?.error ? Variants.invalid : Variants.valid;
}

function validateStripe(value: StripeElementValue): string | null {
  return value?.complete ? null : "Field is incomplete.";
}

export type StripeNewCardProps = {
  isGuestCheckout?: boolean;
  setIsCardComplete(isCardComplete: boolean): void;
  setBillingDetails(billingDetailsToUpdate: BillingDetails): void;
  paymentRequest?: StripeLib.PaymentRequest;
};

export default function StripeNewCard({
  setIsCardComplete,
  setBillingDetails,
  paymentRequest,
  isGuestCheckout = false
}: StripeNewCardProps) {
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const { fields, setters, validateAll, errors } = useFormState(
    {
      number: {
        validators: [validateStripe]
      } as FieldDefinition<StripeElementValue>,
      expiry: {
        validators: [validateStripe]
      } as FieldDefinition<StripeElementValue>,
      cvc: {
        validators: [validateStripe]
      } as FieldDefinition<StripeElementValue>,
      zip: {
        value: "",
        validators: [
          value => (value.length > 0 ? null : "Zipcode is required.")
        ]
      },
      name: {
        value: "",
        validators: isGuestCheckout
          ? [value => (value.length > 0 ? null : "Full name is required.")]
          : []
      }
    },
    { validateInitialValues: false }
  );

  React.useEffect(() => {
    if (hasLoaded) {
      setIsCardComplete(!validateAll().hasErrors);
      const billingDetails = {
        address: {
          postal_code: fields.zip.value
        }
      };
      if (isGuestCheckout) {
        billingDetails["name"] = fields.name.value;
      }
      setBillingDetails(billingDetails);
    } else {
      setHasLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields.number.value?.complete,
    fields.expiry.value?.complete,
    fields.cvc.value?.complete,
    fields.zip.value,
    fields.name.value
  ]);

  const { t } = useTranslation("client\\components\\payments\\StripeNewCard");

  return (
    <React.Fragment>
      {paymentRequest && (
        <Box>
          <StripePaymentRequestButton paymentRequest={paymentRequest} />
          <HorizontalLineWithContent>
            {t`Or pay with credit card`}
          </HorizontalLineWithContent>
        </Box>
      )}
      {isGuestCheckout && (
        <Box
          sx={{
            height: ROW_HEIGHT
          }}
        >
          <LabelBox>{t`Full Name`}</LabelBox>
          <StripeCardBuyerName
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setters.name(e.target.value)
            }
            value={fields.name.value}
          />
          {/* @ts-ignore TS(2322): Type 'FieldError | undefined' is not assignable to... Remove this comment to see the full error message */}
          <FieldErrorMessage value={errors.name?.[0]} />
        </Box>
      )}
      <Box
        sx={{
          height: ROW_HEIGHT
        }}
      >
        <LabelBox>{t`Card Number`}</LabelBox>
        <StripeCardNumber
          variant={getVariant(fields.number)}
          onChange={setters.number}
        />
        {/* @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        <FieldErrorMessage value={fields.number.value?.error?.message} />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          height: ROW_HEIGHT
        }}
      >
        <Box
          sx={{
            flex: 1,
            paddingRight: "0.5em"
          }}
        >
          <LabelBox>{t`Expiration`}</LabelBox>
          <StripeCardExpiry
            variant={getVariant(fields.expiry)}
            onChange={setters.expiry}
          />
          {/* @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
          <FieldErrorMessage value={fields.expiry.value?.error?.message} />
        </Box>
        <Box
          sx={{
            flex: 1,
            paddingLeft: "0.5em"
          }}
        >
          <LabelBox>{t`CVC`}</LabelBox>
          <StripeCardCvc
            variant={getVariant(fields.cvc)}
            onChange={setters.cvc}
          />
          {/* @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
          <FieldErrorMessage value={fields.cvc.value?.error?.message} />
        </Box>
      </Box>
      <Box
        sx={{
          paddingBottom: "1em",
          height: ROW_HEIGHT
        }}
      >
        <LabelBox>{t`ZIP`}</LabelBox>
        <StripeCardZip
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setters.zip(e.target.value)
          }
          value={fields.zip.value}
        />
      </Box>
    </React.Fragment>
  );
}
